import React, { useCallback, useMemo } from "react";

import { Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../../context/StateContext";
import { Carrier } from "../../../../../../models/carrier";
import { Customer } from "../../../../../../models/customer";
import { Port } from "../../../../../../models/port";
import { Project } from "../../../../../../models/project";
import { ShipperEntity } from "../../../../../../models/shipments";
import { Shipper } from "../../../../../../types";
import {
  ShipmentDetails,
  ShipmentTrackingStatus,
  ShipmentType,
  ShipperType,
} from "../../../../../../util/enums";
import { ShipmentObjects } from "../../../../../Booking/util";
import { Input, Select } from "../../../../../Form";
import AutoSuggest from "../../../../../Form/AutoSuggest";
import MapInput from "../../../../../Form/MapInput";
import SkeletonComponent from "../../../../../Generic/Skeleton";
import getShipmentOptions from "../../../../hooks/getShipmentOptions";
import { useShipmentResourcesContext } from "../../../../hooks/useResourceContext";
import { useShipmentContext } from "../../../../hooks/useShipmentContext";
import { BaseFieldProps, ShipmentKey } from "../../../../types";
import { ShipperSelect } from "./ShipperSelect";

interface Props {
  viewOnly?: boolean;
  generateBaseFieldProps: (name: ShipmentKey) => BaseFieldProps<ShipmentKey>;
}

const ShipmentDetailsSection = (props: Props) => {
  const { generateBaseFieldProps, viewOnly } = props;
  const { t: translate } = useTranslation();
  const { isFMVersion } = useAppState();

  const { shipment, handleUpdateShipment: update, handleChangeOptions } = useShipmentContext();
  const {
    portList: portListData,
    supplierList,
    customerList,
    projectList,
    carrierList,
    loading,
  } = useShipmentResourcesContext();

  const isPortTypeShipment = useMemo(
    () =>
      shipment?.[ShipmentDetails.SHIPMENT_TYPE] === ShipmentType.SEA ||
      shipment?.[ShipmentDetails.SHIPMENT_TYPE] === ShipmentType.AIR,
    [shipment?.[ShipmentDetails.SHIPMENT_TYPE]]
  );

  const carrierListByShipmentType = useMemo(() => {
    if (!carrierList || !carrierList.length) return [];

    switch (shipment?.[ShipmentDetails.SHIPMENT_TYPE]) {
      case ShipmentType.AIR:
        return carrierList.filter((carrier) => carrier.type === ShipmentType.AIR);
      case ShipmentType.SEA:
        return carrierList.filter((carrier) => carrier.type === ShipmentType.SEA);
      default:
        return carrierList;
    }
  }, [shipment?.[ShipmentDetails.SHIPMENT_TYPE]]);

  const getCarrierLabel = (carrier: Carrier) => {
    if (carrier.scac === carrier.name) return `${carrier.name}`;

    return `${carrier.name} - ${carrier.scac ?? ""}` || "";
  };

  const { boundOptions } = getShipmentOptions(isFMVersion);

  const handleUpdateShipment = (attribute: ShipmentKey, value: any) => {
    update(attribute, value);
    handleChangeOptions(ShipmentObjects.SHIPMENT);
  };

  const portList = useMemo(() => {
    if (!portListData || !portListData?.length) return [];

    switch (shipment?.shipmentType) {
      case ShipmentType.AIR:
        return portListData.filter((port) => port.site === ShipmentType.AIR.toUpperCase());
      default:
        return portListData.filter((port) => port.site !== ShipmentType.AIR.toUpperCase());
    }
  }, [shipment?.shipmentType, portListData]);

  const loadingPortSelected = portList.find(
    (port) => Number(shipment?.portOfLoadingId) === Number(port.id)
  );

  const dischargePortSelected = portList.find(
    (port) => Number(shipment?.portOfDischargeId) === Number(port.id)
  );

  const portTypeSelected = !shipment?.[ShipmentDetails.SHIPMENT_TYPE] || isPortTypeShipment;

  const consigneeSelected =
    customerList.find((customer) => shipment?.consigneeId === customer.id) || shipment?.consignee;
  const projectSelected = projectList.find((project) => shipment?.projectId === project.id);
  const carrierSelected = carrierList.find((carrier) => shipment?.carrierId === carrier.id);

  const shipperList: Shipper[] = useMemo(() => {
    const customersToShippers: Shipper[] =
      customerList?.map(({ id, fullName, code }) => ({
        id,
        fullName: fullName || "",
        type: ShipperType.CONSIGNEE,
        code: code || "",
      })) || [];

    const supplierToShippers: Shipper[] =
      supplierList?.map(({ id, name, code }) => ({
        id,
        fullName: name || "",
        type: ShipperType.SUPPLIER,
        code: code || "",
      })) || [];

    return [...customersToShippers, ...supplierToShippers]?.sort((a, b) =>
      a.fullName?.localeCompare(b.fullName)
    );
  }, [supplierList, customerList]);

  const shipperSelected: Shipper | undefined = useMemo(() => {
    const { customerId, supplierId } = shipment?.shipperEntity || {};

    if (customerId) {
      return shipperList?.find(
        (shipper) => shipper?.id === customerId && shipper.type === ShipperType.CONSIGNEE
      );
    }

    if (supplierId) {
      return (
        shipperList?.find(
          (shipper) => shipper?.id === supplierId && shipper.type === ShipperType.SUPPLIER
        ) ||
        ({
          id: shipment?.shipperEntity?.supplierId,
          type: ShipperType.SUPPLIER,
          fullName: shipment?.shipperEntity?.fullName,
          code: shipment?.shipperEntity?.code,
        } as Shipper)
      );
    }
  }, [shipment?.shipperEntity, shipperList]);

  const renderSeaFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.VESSEL_NAME)}
            label={`${translate("vessel")}`}
            value={shipment?.[ShipmentDetails.VESSEL_NAME]}
            placeholder=""
            onChange={(event) =>
              handleUpdateShipment(ShipmentDetails.VESSEL_NAME, event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.VOYAGE_NUMBER)}
            label={`${translate("voyage")}`}
            value={shipment?.[ShipmentDetails.VOYAGE_NUMBER]}
            placeholder=""
            onChange={(event) =>
              handleUpdateShipment(ShipmentDetails.VOYAGE_NUMBER, event.target.value)
            }
          />
        </Grid>
      </>
    );
  };

  const renderAirFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.FLIGHT_NUMBER)}
            label={`${translate("flight")} #`}
            value={shipment?.[ShipmentDetails.FLIGHT_NUMBER]}
            placeholder=""
            onChange={(event) =>
              handleUpdateShipment(ShipmentDetails.FLIGHT_NUMBER, event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.AIRLINE)}
            label={`${translate("airline")}`}
            value={shipment?.[ShipmentDetails.AIRLINE]}
            placeholder=""
            onChange={(event) => handleUpdateShipment(ShipmentDetails.AIRLINE, event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.AIRWAY_BILL)}
            label={`${translate("houseAirWaybill")} #`}
            value={shipment?.[ShipmentDetails.AIRWAY_BILL]}
            placeholder=""
            onChange={(event) =>
              handleUpdateShipment(ShipmentDetails.AIRWAY_BILL, event.target.value)
            }
          />
        </Grid>
      </>
    );
  };

  const renderFieldsByMode = useCallback(() => {
    switch (shipment?.shipmentType) {
      case ShipmentType.SEA:
        return renderSeaFields();
      case ShipmentType.AIR:
        return renderAirFields();
      default:
        return null;
    }
  }, [
    shipment?.shipmentType,
    shipment?.[ShipmentDetails.VESSEL_NAME],
    shipment?.[ShipmentDetails.VOYAGE_NUMBER],
    shipment?.[ShipmentDetails.FLIGHT_NUMBER],
    shipment?.[ShipmentDetails.AIRLINE],
    shipment?.[ShipmentDetails.AIRWAY_BILL],
  ]);

  return (
    <>
      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          {...generateBaseFieldProps(ShipmentDetails.BOOKING_NO)}
          label={`${translate("booking")} #`}
          value={shipment?.[ShipmentDetails.BOOKING_NO]}
          placeholder=""
          disabled={
            ((!!shipment?.shipmentTrackingId || !!shipment?.bookingId) &&
              !(
                shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() ===
                ShipmentTrackingStatus.TRACKING_NOT_AVAILABLE
              )) ||
            viewOnly
          }
          onChange={(event) => handleUpdateShipment(ShipmentDetails.BOOKING_NO, event.target.value)}
          required
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          {...generateBaseFieldProps(ShipmentDetails.MASTER_BILL)}
          label={`${translate("masterbillOfLanding")} #`}
          value={shipment?.[ShipmentDetails.MASTER_BILL]}
          placeholder=""
          onChange={(event) =>
            handleUpdateShipment(ShipmentDetails.MASTER_BILL, event.target.value)
          }
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          {...generateBaseFieldProps(ShipmentDetails.BILL_OF_LANDING)}
          label={`${translate("billOfLanding")} #`}
          value={shipment?.[ShipmentDetails.BILL_OF_LANDING]}
          placeholder=""
          required
          disabled={
            (!!shipment?.shipmentTrackingId &&
              !(
                shipment?.shipmentTracking?.shipmentStatus?.toLowerCase() ===
                ShipmentTrackingStatus.TRACKING_NOT_AVAILABLE
              )) ||
            viewOnly
          }
          onChange={(event) =>
            handleUpdateShipment(ShipmentDetails.BILL_OF_LANDING, event.target.value)
          }
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          {...generateBaseFieldProps(ShipmentDetails.HOUSE_BILL)}
          label={`${translate("housebillOfLanding")} #`}
          value={shipment?.[ShipmentDetails.HOUSE_BILL]}
          placeholder=""
          onChange={(event) => handleUpdateShipment(ShipmentDetails.HOUSE_BILL, event.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={portTypeSelected ? 3 : 6} lg={portTypeSelected ? 3 : 6}>
        <MapInput
          {...generateBaseFieldProps(ShipmentDetails.ORIGIN_LOCATION)}
          required={!shipment?.bookingId}
          inputValue={shipment?.[ShipmentDetails.ORIGIN_LOCATION]?.longAddress}
          onChange={(location) =>
            handleUpdateShipment("all", {
              [ShipmentDetails.ORIGIN]: location?.longAddress,
              [ShipmentDetails.ORIGIN_LOCATION]: location,
            })
          }
          showMapIcon={false}
          disabled={viewOnly}
          label={translate("placeOfOrigin")}
          placeholder={translate("typeInToSearch")}
          noOptionsText={translate("searchAddress")}
          removeArrow={true}
        />
      </Grid>

      {portTypeSelected && (
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <SkeletonComponent isLoading={loading}>
            <AutoSuggest
              {...generateBaseFieldProps(ShipmentDetails.PORT_OF_LOADING_ID)}
              freeSolo
              removeShadowBox={false}
              label={`${translate("portOfLoading")} (PoL)`}
              placeholder={translate("typeInToSearch")}
              disabled={!shipment?.shipmentType || !!shipment?.bookingId || viewOnly}
              inputValue={loadingPortSelected?.siteCode}
              options={portList}
              getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
              getOptionLabel={(port: Port) => port.siteCode || ""}
              onChange={(port: Port) => {
                if (port && port.id) {
                  handleUpdateShipment("all", {
                    [ShipmentDetails.PORT_OF_LOADING_ID]: port.id,
                    [ShipmentDetails.PORT_OF_LOADING]: port,
                  });
                } else {
                  handleUpdateShipment("all", {
                    [ShipmentDetails.PORT_OF_LOADING_ID]: null,
                    [ShipmentDetails.PORT_OF_LOADING]: null,
                  });
                }
              }}
            />
          </SkeletonComponent>
        </Grid>
      )}

      {portTypeSelected && (
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <SkeletonComponent isLoading={loading}>
            <AutoSuggest
              {...generateBaseFieldProps(ShipmentDetails.PORT_OF_DISCHARGE_ID)}
              freeSolo
              removeShadowBox={false}
              label={`${translate("portOfDischarge")} (PoD)`}
              placeholder={translate("typeInToSearch")}
              disabled={viewOnly || !shipment?.shipmentType || !!shipment?.bookingId}
              inputValue={dischargePortSelected?.siteCode}
              options={portList}
              getOptionSelected={(option?: Port, value?: Port) => option?.id === value?.id}
              getOptionLabel={(port: Port) => port.siteCode || ""}
              onChange={(port: Port) => {
                if (port && port.id) {
                  handleUpdateShipment("all", {
                    [ShipmentDetails.PORT_OF_DISCHARGE_ID]: port.id,
                    [ShipmentDetails.PORT_OF_DISCHARGE]: port,
                  });
                } else {
                  handleUpdateShipment("all", {
                    [ShipmentDetails.PORT_OF_DISCHARGE_ID]: null,
                    [ShipmentDetails.PORT_OF_DISCHARGE]: null,
                  });
                }
              }}
            />
          </SkeletonComponent>
        </Grid>
      )}

      <Grid item xs={12} sm={6} md={portTypeSelected ? 3 : 6} lg={portTypeSelected ? 3 : 6}>
        <MapInput
          {...generateBaseFieldProps(ShipmentDetails.DESTINATION_LOCATION)}
          required={!shipment?.bookingId}
          inputValue={shipment?.[ShipmentDetails.DESTINATION_LOCATION]?.longAddress}
          onChange={(location) =>
            handleUpdateShipment("all", {
              [ShipmentDetails.DESTINATION]: location?.longAddress,
              [ShipmentDetails.DESTINATION_LOCATION]: location,
            })
          }
          showMapIcon={false}
          disabled={viewOnly}
          label={translate("placeOfDestination")}
          placeholder={translate("typeInToSearch")}
          noOptionsText={translate("searchAddress")}
          removeArrow={true}
        />
      </Grid>

      <Grid item xs={12} sm={12} md={6} lg={6}>
        <SkeletonComponent isLoading={loading}>
          <AutoSuggest
            {...generateBaseFieldProps(ShipmentDetails.SHIPPER_ENTITY)}
            freeSolo
            removeShadowBox={false}
            required
            label={translate("shipper")}
            placeholder={translate("typeInToSearch")}
            inputValue={shipperSelected?.fullName}
            options={shipperList}
            disabled={viewOnly || !!shipment?.bookingId}
            renderOption={(props, option: Shipper) => {
              return (
                <div {...props}>
                  <ShipperSelect shipper={option} />
                </div>
              );
            }}
            getOptionSelected={(option?: Shipper, value?: Shipper) =>
              option?.id === value?.id && option?.type === value?.type
            }
            getOptionLabel={(option: Shipper) => option?.fullName || ""}
            onChange={(option: Shipper) => {
              if (option?.id) {
                const value: ShipperEntity =
                  option?.type === ShipperType.CONSIGNEE
                    ? {
                        customerId: option?.id,
                        supplierId: null,
                        fullName: option?.fullName,
                        code: option?.code,
                      }
                    : {
                        supplierId: option?.id,
                        customerId: null,
                        fullName: option?.fullName,
                        code: option?.code,
                      };

                handleUpdateShipment(ShipmentDetails.SHIPPER_ENTITY, value);
              } else {
                handleUpdateShipment(ShipmentDetails.SHIPPER_ENTITY, null);
              }
            }}
          />
        </SkeletonComponent>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          disabled
          label={`${translate("shipperCode")} #`}
          value={shipperSelected?.code || ""}
          placeholder=""
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Select
          {...generateBaseFieldProps(ShipmentDetails.BOUND)}
          displayEmpty
          placeholder={translate("select")}
          label={translate("shipment")}
          value={shipment?.bound || ""}
          onChange={(event) => handleUpdateShipment(ShipmentDetails.BOUND, event.target.value)}
        >
          {boundOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>

      <Grid item xs={12} sm={12} md={6} lg={6}>
        <SkeletonComponent isLoading={loading}>
          <AutoSuggest
            {...generateBaseFieldProps(ShipmentDetails.CONSIGNEE_ID)}
            freeSolo
            removeShadowBox={false}
            required
            disabled={viewOnly || !!shipment?.bookingId}
            label={translate("consignee")}
            placeholder={translate("typeInToSearch")}
            inputValue={consigneeSelected?.name}
            options={customerList}
            getOptionSelected={(option?: Customer, value?: Customer) => option?.id === value?.id}
            getOptionLabel={(option: Customer) => option.name || ""}
            onChange={(option: Customer) => {
              if (option && option.id) {
                handleUpdateShipment(ShipmentDetails.CONSIGNEE_ID, option.id);
              } else {
                handleUpdateShipment(ShipmentDetails.CONSIGNEE_ID, null);
              }
            }}
          />
        </SkeletonComponent>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <Input
          disabled
          label={`${translate("consigneeCode")} #`}
          value={consigneeSelected?.code || ""}
          placeholder=""
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <SkeletonComponent isLoading={loading}>
          <AutoSuggest
            {...generateBaseFieldProps(ShipmentDetails.PROJECT_ID)}
            freeSolo
            removeShadowBox={false}
            required
            label={translate("project")}
            placeholder={translate("typeInToSearch")}
            inputValue={projectSelected?.name}
            options={projectList}
            getOptionSelected={(option?: Project, value?: Project) => option?.id === value?.id}
            getOptionLabel={(option: Project) => option.name || ""}
            onChange={(option: Project) => {
              if (option && option.id) {
                handleUpdateShipment("all", {
                  [ShipmentDetails.PROJECT_ID]: option.id,
                  [ShipmentDetails.EVENT_TYPE]: option.name,
                });
              } else {
                handleUpdateShipment("all", {
                  [ShipmentDetails.PROJECT_ID]: null,
                  [ShipmentDetails.EVENT_TYPE]: null,
                });
              }
            }}
          />
        </SkeletonComponent>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={3}>
        <SkeletonComponent isLoading={loading}>
          <AutoSuggest
            {...generateBaseFieldProps(ShipmentDetails.CARRIER_ID)}
            freeSolo
            removeShadowBox={false}
            required
            label={translate("carrier")}
            placeholder={translate("typeInToSearch")}
            inputValue={carrierSelected?.name}
            options={carrierListByShipmentType}
            disabled={
              !!shipment?.shipmentTrackingId ||
              !shipment?.[ShipmentDetails.SHIPMENT_TYPE] ||
              viewOnly
            }
            getOptionSelected={(option?: Carrier, value?: Carrier) => option?.id === value?.id}
            getOptionLabel={getCarrierLabel}
            onChange={(option: Carrier) => {
              if (option && option.id) {
                handleUpdateShipment(ShipmentDetails.CARRIER_ID, option.id);
                handleUpdateShipment(ShipmentDetails.CARRIER_SCAC, option.scac);
              } else {
                handleUpdateShipment(ShipmentDetails.CARRIER_ID, null);
                handleUpdateShipment(ShipmentDetails.CARRIER_SCAC, null);
              }
            }}
          />
        </SkeletonComponent>
      </Grid>
      {renderFieldsByMode()}
    </>
  );
};

export default ShipmentDetailsSection;
